import { FocusEvent, FunctionComponent, useCallback } from "react";
import { faXmarkLarge } from "@fortawesome/pro-solid-svg-icons";
import classnames from "classnames";
import { ActionIcon } from "../action-icon";
import { Box } from "../box";
import { Group } from "../group";
import { Icon } from "../icon";
import { Popover } from "../popover";
import { ScrollArea } from "../scroll-area";
import { Text } from "../text";
import { POPOVER_INFO_CARD_BODY_MAX_HEIGHT } from "./popover-info-card.constants";
import styles from "./popover-info-card.module.scss";
import type { IBodyProps, IDropdownProps, IFooterProps, IHeaderProps } from "./popover-info-card.types";

/**
 * Marks whatever takes focus inside the card the way Mantine marks its menu items, so a modal or drawer around the
 * card ignores the Escape that closes the card. Mantine checks the key event's target from a window listener that runs
 * before any handler here could stop the event.
 * @param event The focus event from inside the card.
 */
const stopEscapeReachingParents = ({ target }: FocusEvent<HTMLDivElement>) =>
    target.setAttribute("data-mantine-stop-propagation", "true");

/**
 * The card itself: a light, bordered surface, as InfoCard's.
 * @param props.className Added to the card's own class.
 */
export const Dropdown: FunctionComponent<IDropdownProps> = ({ children, className, onFocusCapture, ...props }) => {
    const onFocusInside = useCallback(
        (event: FocusEvent<HTMLDivElement>) => {
            stopEscapeReachingParents(event);
            onFocusCapture?.(event);
        },
        [onFocusCapture]
    );
    return (
        <Popover.Dropdown
            p={0}
            {...props}
            // Merged, not replaced: the card's surface rules live on this class, and Escape handling on the handler.
            className={classnames(styles["popover-info-card-dropdown"], className)}
            data-mantine-stop-propagation
            onFocusCapture={onFocusInside}
        >
            <Box className={styles["popover-info-card-content"]} bdrs="lg">
                {children}
            </Box>
        </Popover.Dropdown>
    );
};

/**
 * The card's title row, above the body, with a close button when it is given `onClose`.
 * @param props.onClose          Called by the close button.
 * @param props.closeButtonLabel The close button's accessible name.
 */
export const Header: FunctionComponent<IHeaderProps> = ({ children, onClose, closeButtonLabel = "Close" }) => (
    <Group justify="space-between" wrap="nowrap" gap="lg" px="lg" py="md">
        <Text size="sm" variant="medium" className={styles["popover-info-card-header"]}>
            {children}
        </Text>
        {typeof onClose === "function" && (
            <ActionIcon variant="subtle" color="gray" size="input-xs" aria-label={closeButtonLabel} onClick={onClose}>
                <Icon size="sm" icon={faXmarkLarge} />
            </ActionIcon>
        )}
    </Group>
);

/**
 * The card's main content: a bordered section with rounded top corners, sitting under the header when there is one.
 * Past its maximum height it scrolls.
 */
export const Body: FunctionComponent<IBodyProps> = ({ children }) => (
    <Box
        className={styles["popover-info-card-body"]}
        bd="1px solid var(--mantine-color-border-default)"
        bdrs="var(--mantine-radius-lg) var(--mantine-radius-lg) 0 0"
        mx={-1}
        mb={-1}
    >
        <ScrollArea.Autosize mah={POPOVER_INFO_CARD_BODY_MAX_HEIGHT}>
            <Box p="lg">{children}</Box>
        </ScrollArea.Autosize>
    </Box>
);

/**
 * The card's action row, right-aligned below the body. The body's bottom border is its divider.
 */
export const Footer: FunctionComponent<IFooterProps> = ({ children }) => (
    <Group justify="flex-end" gap="sm" px="lg" py="sm">
        {children}
    </Group>
);
